import { useEffect, useState, useMemo, useCallback } from 'react';
import Pubnub from 'pubnub';
import config from '../config';

export default function useConnectedScreens(
  pubnubClient: Pubnub,
  channelId: string,
  sessionId: string,
  logger: Logger,
): number | null {
  const [connectedScreens, setConnectedScreens] = useState<number | null>(null);

  const makeRequest = useCallback(() => {
    pubnubClient.hereNow(
      {
        channels: [channelId],
        includeUUIDs: true,
      },
      (status, response) => {
        if (status.error) {
          // Silently fail, don't block playback if hereNow fails.
          logger.error(
            `Failed to fetch connected screens with '${JSON.stringify(
              status.errorData,
            )}'`,
          );
        } else {
          const occupants = response.channels[channelId]?.occupants ?? [];
          const otherOccupants = occupants.filter((o) => o.uuid !== sessionId);
          if (otherOccupants.length > 0) {
            logger.warn(
              `Found '${
                otherOccupants.length
              }' other connected screens, mySessionId=${sessionId} otherSessionIds=${otherOccupants
                .map((o) => o.uuid)
                .join(',')}`,
            );
          }
          setConnectedScreens(otherOccupants.length);
        }
      },
    );
  }, [pubnubClient, setConnectedScreens, channelId, sessionId, logger]);

  // Handle presence events.
  const handleMessage = useMemo(
    () => ({
      presence: (event: Pubnub.PresenceEvent) => {
        logger.info(
          `Received presence event, action=${event.action} uuid=${event.uuid}`,
        );
        if (event.uuid === sessionId) return;
        makeRequest();
      },
    }),
    [makeRequest, sessionId, logger],
  );

  // Fetch connected screens.
  useEffect(() => {
    // Delay fetching connected screens initially until after the pubnub
    // presence timeout. This allows any connections to timeout properly to
    // avoid showing the multiple screens error when reloading the page.
    const initialRequestDelayMs =
      (config.pubnubPresenceTimeoutSeconds + 5) * 1000;

    let interval: ReturnType<typeof setInterval>;
    const timeout = setTimeout(() => {
      // Listen for presence events after initial request.
      pubnubClient.addListener(handleMessage);
      // Poll as a backup if presence events aren't received.
      interval = setInterval(makeRequest, 30000);
    }, initialRequestDelayMs);

    return () => {
      if (timeout) clearTimeout(timeout);
      if (interval) clearInterval(interval);
      pubnubClient.removeListener(handleMessage);
    };
  }, [makeRequest, handleMessage, pubnubClient]);

  return connectedScreens;
}
